import { createContext, ReactNode, useContext } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { Icon } from "./Icon";
import { useAuth } from "../../stores/auth";

// The Shell mounts a fixed topbar host as a flex sibling of the scroller and
// hands its DOM node down through this context. TopBar portals into it so the
// bar lives OUTSIDE the scroll container — iOS standalone WebViews drop
// position:sticky inside an overflow scroller, letting the bar scroll away.
export const TopBarSlotContext = createContext<HTMLElement | null>(null);

type Props = {
  title?: string;
  sub?: string;
  back?: boolean;
  onBack?: () => void;
  right?: ReactNode;
};

function initials(name: string | null | undefined, fallback: string): string {
  const src = (name || "").trim();
  if (!src) return fallback;
  const parts = src.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function TopBar({ title, sub, back, onBack, right }: Props) {
  const user = useAuth((s) => s.user);
  const slot = useContext(TopBarSlotContext);
  const userName = user?.display_name?.split(" ")[0] || user?.username || "—";
  const userInitials = initials(user?.display_name || user?.username, "FB");

  const bar = (
    <div className="tcg-topbar">
      <div className="tcg-topbar-brand">
        {back ? (
          <button className="tcg-back-btn" onClick={onBack} aria-label="indietro">
            <Icon name="chev-l" />
          </button>
        ) : (
          <Link to="/" className="tcg-logo-dot" aria-label="home">
            <img src="/assets/tcg-card-mark.png" alt="TCG" />
          </Link>
        )}
        <div style={{ minWidth: 0 }}>
          <div className="tcg-brand-name">{title || "TCG Hub"}</div>
          <div className="tcg-brand-sub">{sub || "Inventario · Sede Vicenza"}</div>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {right}
        <Link to="/settings" className="tcg-user-pill" aria-label="impostazioni">
          <span className="tcg-user-avatar">{userInitials}</span>
          <span className="tcg-user-name">{userName}</span>
        </Link>
      </div>
    </div>
  );

  return slot ? createPortal(bar, slot) : bar;
}
